import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import Header from '../components/Header';
import Hero from '../components/Hero';
import ProductSection from '../components/ProductSection';
import AboutSection from '../components/AboutSection';
import Footer from '../components/Footer';
import CartDrawer from '../components/CartDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CafePlaceholder = ({ status, onRetry }) => (
  <section
    id="cafe"
    data-testid="cafe-placeholder"
    className="py-24 bg-[#FDFBF7] min-h-[60vh] flex items-center justify-center"
  >
    <div className="max-w-xl mx-auto px-6 text-center">
      <p className="text-[#B58A3F] uppercase tracking-[0.3em] text-sm mb-4 font-medium">
        Huila, Colombia
      </p>
      <h2 className="heading-serif text-4xl md:text-5xl text-[#142A1D] tracking-tight font-light mb-8">
        Nuestro Café
      </h2>

      {status === 'loading' && (
        <div
          data-testid="cafe-loading"
          className="flex flex-col items-center gap-4 text-[#142A1D] opacity-80"
        >
          <Loader2 className="w-10 h-10 text-[#B58A3F] animate-spin" strokeWidth={1.5} />
          <p className="text-base">Cargando nuestro café...</p>
        </div>
      )}

      {status === 'error' && (
        <div
          data-testid="cafe-error"
          className="flex flex-col items-center gap-5 text-[#142A1D]"
        >
          <AlertCircle className="w-10 h-10 text-[#B58A3F]" strokeWidth={1.5} />
          <p className="text-base opacity-80 leading-relaxed">
            No pudimos cargar nuestro café en este momento. Por favor, intenta de nuevo en unos segundos.
          </p>
          <button
            data-testid="cafe-retry-button"
            onClick={onRetry}
            className="btn-primary inline-flex items-center gap-2 px-8 py-3 text-base"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={2} />
            Reintentar
          </button>
        </div>
      )}

      {status === 'empty' && (
        <div
          data-testid="cafe-empty"
          className="flex flex-col items-center gap-5 text-[#142A1D]"
        >
          <p className="text-base opacity-80 leading-relaxed">
            Estamos preparando algo especial. Vuelve muy pronto para descubrir nuestro café de especialidad del Huila.
          </p>
          <a
            href="#contacto"
            className="btn-primary inline-block px-8 py-3 text-base"
          >
            Contáctanos
          </a>
        </div>
      )}
    </div>
  </section>
);

const Home = () => {
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error
  const [cartOpen, setCartOpen] = useState(false);

  const fetchProduct = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await axios.get(`${API}/products`);
      if (response.data && response.data.length > 0) {
        setProduct(response.data[0]);
        setStatus('ready');
      } else {
        setProduct(null);
        setStatus('empty');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setProduct(null);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header onCartClick={() => setCartOpen(true)} />
      <Hero />
      {status === 'ready' && product ? (
        <ProductSection product={product} />
      ) : (
        <CafePlaceholder status={status} onRetry={fetchProduct} />
      )}
      <AboutSection />
      <Footer />
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </div>
  );
};

export default Home;
